import * as api from '@gitbook/api-client';

export type StepperType = 'old-pricing' | 'new-pricing' | 'community';

export type StepperInputs<T extends StepperType> = T extends 'old-pricing'
    ? OldPricingInputs
    : T extends 'new-pricing'
      ? NewPricingInputs
      : T extends 'community'
        ? CommunityInputs
        : never;

export type OldPricingInputs = {
    name: string;
    content: 'sample' | 'empty' | null;
    publish: 'now' | 'later' | null;
};

export type NewPricingInputs = {
    name: string;
    content: 'sample' | 'empty' | null;
    features: {
        plan:
            | api.SiteType.Basic
            | api.SiteType.Premium
            | api.SiteType.Ultimate
            | api.SiteType.Sponsored;
        previewPublishOutcome: api.OperationSpec<'previewSitePublishCheckout'>['ResponseBody'];
    };
    publish: 'now' | 'later' | null;
    withVA?: boolean;
};

export type CommunityInputs = {
    name: string;
    content: 'sample' | 'empty' | null;
    features: {
        plan: api.SiteType.Sponsored;
        previewPublishOutcome: api.OperationSpec<'previewSitePublishCheckout'>['ResponseBody'];
    };
    publish: 'now' | 'later' | null;
};

export function isNewPricingInputs(
    stepperType: StepperType,
    inputs: StepperInputs<StepperType>
): inputs is NewPricingInputs {
    return stepperType === 'new-pricing' && 'features' in inputs;
}

export const oldPricingSteps = [
    {
        id: 'name',
        index: 1,
        title: 'Name',
    },
    {
        id: 'content',
        index: 2,
        title: 'Content',
    },
    {
        id: 'publish',
        index: 3,
        title: 'Publish',
    },
] as const;

export const newPricingSteps = [
    {
        id: 'name',
        index: 1,
        title: 'Name',
    },
    {
        id: 'content',
        index: 2,
        title: 'Content',
    },
    {
        id: 'features',
        index: 3,
        title: 'Features',
    },
    {
        id: 'publish',
        index: 4,
        title: 'Publish',
    },
] as const;

export const communitySteps = [
    {
        id: 'name',
        index: 1,
        title: 'Name',
    },
    {
        id: 'content',
        index: 2,
        title: 'Content',
    },
    {
        id: 'publish',
        index: 3,
        title: 'Publish',
    },
] as const;

export type StepId<T extends StepperType> = T extends 'old-pricing'
    ? OldPricingStepId
    : T extends 'new-pricing'
      ? NewPricingStepId
      : T extends 'community'
        ? CommunityStepId
        : never;
export type OldPricingStepId = (typeof oldPricingSteps)[number]['id'];
export type NewPricingStepId = (typeof newPricingSteps)[number]['id'];
export type CommunityStepId = (typeof communitySteps)[number]['id'];

export type StepperState<T extends StepperType> = {
    step: StepId<T>;
    inputs: StepperInputs<T>;
    dirty: boolean;
};

export type StepperAction<T extends StepperType> =
    | {
          type: 'submit';
          step: StepId<T>;
          inputs: Partial<StepperInputs<T>>;
      }
    | {
          type: 'update';
          inputs: Partial<StepperInputs<T>>;
      }
    | {
          type: 'go';
          step: StepId<T>;
      }
    | {
          type: 'goBack';
      };

export function getSteps<T extends StepperType>(
    stepperType: T
): {
    id: StepId<T>;
    index: number;
    title: string;
}[] {
    switch (stepperType) {
        case 'old-pricing':
            return [...oldPricingSteps] as { id: StepId<T>; index: number; title: string }[];
        case 'new-pricing':
            return [...newPricingSteps] as { id: StepId<T>; index: number; title: string }[];
        case 'community':
            return [...communitySteps] as { id: StepId<T>; index: number; title: string }[];
        default:
            throw new Error(`Unknown stepper type: ${stepperType}`);
    }
}

export function getInitialState<T extends StepperType>(stepperType: T): StepperState<T> {
    switch (stepperType) {
        case 'old-pricing':
            return {
                step: 'name',
                inputs: { name: '', content: 'sample', publish: 'now' },
                dirty: false,
            } as StepperState<T>;
        case 'new-pricing':
            return {
                step: 'name',
                inputs: {
                    name: '',
                    content: 'sample',
                    features: { plan: api.SiteType.Premium },
                    publish: 'now',
                },
                dirty: false,
            } as StepperState<T>;
        case 'community':
            return {
                step: 'name',
                inputs: {
                    name: '',
                    content: 'sample',
                    features: { plan: api.SiteType.Sponsored },
                    publish: 'now',
                },
                dirty: false,
            } as StepperState<T>;
        default:
            throw new Error(`Unknown stepper type: ${stepperType}`);
    }
}
